import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseMapComponent,
  DrawingManagerService,
  MapFilterService,
  MapService,
  MapToolbarComponent,
  Polygon,
  TrackplayComponent,
} from '@vectronic/base-map';
import { dsDtos, userDtos, webDtos } from '@vectronic/openApi-admin';
import { AlertComponent, defaultPreferences, LeftSidebarComponent } from '@vectronic/ui';
import {
  DialogService,
  Item,
  MobileService,
  SelectableDeviceIds,
  SnackbarService,
} from '@vectronic/util';
import { combineLatest, debounceTime, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DeleteDialogComponent } from './delete-dialog/delete-dialog.component';
import { ListViewComponent } from './list-view/list-view.component';

@Component({
  selector: 'sm-shared-map-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    CommonModule,
    BaseMapComponent,
    LeftSidebarComponent,
    ListViewComponent,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
    MatButtonToggleModule,
    MatTooltipModule,
    MatCardModule,
    MapToolbarComponent,
    TrackplayComponent,
  ],
  templateUrl: './shared-map-page.component.html',
  styleUrl: './shared-map-page.component.scss',
})
export class SharedMapPageComponent {
  mapFilterService = inject(MapFilterService);
  mapService = inject(MapService);
  dialogService = inject(DialogService);
  snackbarService = inject(SnackbarService);
  mobileService = inject(MobileService);
  private drawingManager = inject(DrawingManagerService);

  Item = Item;

  @ViewChild('fileInput') fileInput!: ElementRef;
  isLoading = input(false);
  hasMinHeight = input(false);
  allDevices = input<SelectableDeviceIds>();
  speciesMap = input<Map<number, dsDtos.AnimalSpecies>>();
  currentPointTypeInEdit = true;
  isVirtualFenceEnabled = input<boolean>();
  isViewOnlyMode = input<boolean>();
  virtualFenceIds = input(null, {
    transform: (value: number[] | null) => {
      if (!value) return null;
      this.selectedCollarIds.set(value);
      return value;
    },
  });
  hideSidebars = input(false);
  hideSettingsBtn = input<boolean>();
  allPolygons = input<Polygon[]>();
  currentlyEditingFenceId = input<string | null>();
  isEditModeVirtualFence = input<boolean>();
  readonlyVirtualFence = input<boolean>();
  drawHandlerVirtualFence = input<any>();
  isEmptyVirtualFence = input<boolean>();
  hasEmptyVirtualFence = input<boolean>();
  isVirtualFenceFilled = input<boolean>();
  mapPositions = input<webDtos.MapPositionsList[]>([]);
  mapPositionsColorChanged = output<webDtos.MapPositionsList[]>();
  loadPositionsEvent = output<{
    deviceIds: number[];
    lastXRecords?: number;
    utcFrom?: string;
    utcTo?: string;
    isAnimation?: boolean;
  }>();

  pageName = input<string>('map');
  initialLastXRecordsValue = input<number>(50);
  filtersToDisplay = input<string[]>(['utc', 'lastXRecords', 'mapFixType', 'animationPlay']);
  showOverrideWarning = input<boolean>();

  // TODO -> this is a temporary release workaround however it should be removed and pass only needed properties as input
  userPreferences = input(null, {
    transform: (preferences: userDtos.UserPreferences) => {
      const definedPreferences = Object.fromEntries(
        Object.entries(preferences ?? {}).filter(([, value]) => value != null),
      );
      const merged = {
        ...defaultPreferences,
        ...definedPreferences,
      } as any;

      if (!merged.mapMostRecentAmount) {
        merged.mapMostRecentAmount = 50;
      }

      if (merged.lightConditionToggle === undefined) {
        merged.lightConditionToggle = preferences?.mapLightConditionIconToggle ?? false;
      }
      return merged;
    },
  });

  selectedCollarIds = signal<number[]>([]);
  displayedLngs = new Set<number>();

  mapFilters = new Subject<webDtos.PositionRequest>();
  mapFilters$ = this.mapFilters.asObservable();
  selectedCommands = signal<dsDtos.Command[]>(null);
  isAnimationPlayActive = signal(false);
  shouldResetMapZoom = signal(true);
  internalMapPositions = signal<webDtos.MapPositionsList[]>([]);
  hasMapData = computed(() =>
    this.internalMapPositions().some(({ positions }) => positions.length),
  );

  isVirtualFenceEnabledSub = toObservable(this.isVirtualFenceEnabled)
    .pipe(takeUntilDestroyed())
    .subscribe((isEnabled) => {
      if (isEnabled) {
        this.shouldResetMapZoom.set(false);
      }
    });

  fetchPositions$ = combineLatest([
    toObservable(this.selectedCollarIds),
    toObservable(this.mapFilterService.request),
  ]).pipe(
    map(([deviceIds, baseRequest]) => ({ ...baseRequest, deviceIds })),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
  );

  constructor() {
    effect(() => {
      // When the input mapPositions changes, update the internal signal
      this.internalMapPositions.set(this.mapPositions());
    });

    this.fetchPositions$.pipe(takeUntilDestroyed(), debounceTime(500)).subscribe((request) => {
      this.loadPositionsEvent.emit(request);
    });
  }

  private isComponentLoading = signal(false);
  isAnythingLoading = computed(() => this.isLoading() || this.isComponentLoading());

  enableVfButtons = computed(
    () => this.drawingManager.activeDrawingMode() !== 'distance-measurement',
  );

  // virtual fencing
  setEmptyVirtualFence = output();
  cancelNewPolygonCreation = output();
  virtualFenceFileSelected = output<Event>();
  startEditingPolygon = output<{ lat: number; lng: number; currentPointType: boolean }>();
  virtualFencePointChanged = output<{ lat: number; lng: number; event: MatButtonToggleChange }>();
  setFocusOnPolygon = output<{ lat: number; lng: number }>();
  saveEditedPolygon = output<{ lat: number; lng: number }>();
  createNewPolygon = output();
  deletePolygon = output<{ lng: number; lat: number }>();
  resetVirtualFence = output();

  onSelectedCollarsFilterListChange(updatedList: { id: number; checked: boolean }[]) {
    this.selectedCollarIds.set(updatedList.filter((item) => item.checked).map((item) => item.id));
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  setMapPositionAndZoom(event: { collarId: number }) {
    this.mapService.zoomToItem(event.collarId);
  }

  colorChanged(event: { key: number; color: string }) {
    this.internalMapPositions.update((currentMapPositions) => {
      const mapPositionList = currentMapPositions.find((p) => p.id === event.key);
      if (mapPositionList) {
        mapPositionList.color = event.color;
      }
      this.shouldResetMapZoom.set(false);
      return [...currentMapPositions];
    });
  }

  prepareIdsForBECall(collars: { id: number; checked: boolean }[]) {
    this.selectedCollarIds.update(() => {
      const checkedCollars = collars.filter((collar) => collar.checked);
      return checkedCollars.map((collar) => collar.id);
    });
  }

  getUniqueLngs(fences: { insidePoint: { lng: number } }[]): number[] {
    const uniqueLngs = new Set<number>();
    fences.forEach((fence) => uniqueLngs.add(fence.insidePoint.lng));
    return Array.from(uniqueLngs);
  }

  openDeleteDialog(selectedFence: Polygon): void {
    this.dialogService.open(DeleteDialogComponent, {
      data: { selectedFence },
      afterConfirm: (insidePoint) => this.deletePolygon.emit(insidePoint),
    });
  }

  getUniqueId(insidePoint: { lat: number; lng: number }): string {
    return `${insidePoint.lat}|${insidePoint.lng}`;
  }

  onClickSetEmptyVirtualFence() {
    this.setEmptyVirtualFence.emit();
  }

  onStartEditingPolygon(insidePoint: { lat: number; lng: number }, isPointInside: null | boolean) {
    this.startEditingPolygon.emit({ ...insidePoint, currentPointType: isPointInside });
  }

  onVirtualFencePointChanged(
    event: MatButtonToggleChange,
    insidePoint: { lat: number; lng: number },
  ) {
    this.virtualFencePointChanged.emit({ ...insidePoint, event });
  }
}
